import { uniqBy } from 'lodash';
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { AchievementsApi } from '../API/AchievementsApi';
import badgeTier0 from '../assets/achievement-tier-0.svg';
import badgeTier1 from '../assets/achievement-tier-1.svg';
import badgeTier2 from '../assets/achievement-tier-2.svg';
import badgeTier3 from '../assets/achievement-tier-3.svg';
import { useAuth } from '../Auth/AuthProvider';
import { AchievementTier } from '../Types/AchievementTier';
import { UserType } from '../Types/User';
import {
  UserAchievement,
  UserAchievementStatus,
} from '../Types/UserAchievement';
import { UserAchievementTierProgress } from '../Types/UserAchievementTierProgress';
import { AnalyticsEvent, useAnalytics } from './AnalyticsProvider';

const ACHIEVEMENTS_REQUIRED_BY_TIER: Record<AchievementTier, number> = {
  [AchievementTier.Bronze]: 4,
  [AchievementTier.Silver]: 4,
  [AchievementTier.Gold]: 4,
};

interface AchievementsContextType {
  achievements?: UserAchievement[];
  startedAchievements?: UserAchievement[];
  notStartedAchievements?: UserAchievement[];
  registerUserAchievement: (userAchievement: UserAchievement) => void;
  newAchievements: UserAchievement[];
  reloadAchievements: () => void;
  getAchievementImage: (userAchievement: UserAchievement) => string;
  maxUserAchievements: UserAchievement[];
  tierInfo: UserAchievementTierProgress;
  getTierColor: (tier: AchievementTier) => string;
}

const AchievementsContext = createContext<AchievementsContextType | undefined>(
  undefined,
);

interface AchievementsProviderProps {
  children: ReactNode;
}

const getTierColor = (tier: AchievementTier) => {
  switch (tier) {
    case AchievementTier.Bronze:
      return '#c96c29';
    case AchievementTier.Silver:
      return '#9a9a9a';
    case AchievementTier.Gold:
      return '#e0b62d';
    default:
      return '#E5E7EB'; // Default gray for no tier
  }
};

export const AchievementsProvider: React.FC<AchievementsProviderProps> = ({
  children,
}) => {
  const { userId, userType } = useAuth();
  const { trackEvent } = useAnalytics();
  const [notStartedAchievements, setNotStartedAchievements] =
    useState<UserAchievement[]>();
  const [startedAchievements, setStartedAchievements] =
    useState<UserAchievement[]>();
  const [newAchievements, setNewAchievements] = useState<UserAchievement[]>([]);
  const [loadingAt, setLoadingAt] = useState<Date | null>(new Date());

  useEffect(() => {
    if (!userId) return;
    if (!userType) return;

    setNewAchievements([]);
    setStartedAchievements(undefined);
    setNotStartedAchievements(undefined);

    switch (userType) {
      case UserType.Student:
        const achievementsApi = new AchievementsApi();
        Promise.all([
          achievementsApi.getUserAchievements(),
          achievementsApi.getUserUntouchedAchievements(),
        ]).then(([achievements, untouchedAchievements]) => {
          setStartedAchievements(achievements);
          setNotStartedAchievements(untouchedAchievements);
        });
        break;
      case UserType.Parent:
        setStartedAchievements([]);
        setNotStartedAchievements([]);
        break;
    }
  }, [userId, userType, loadingAt]);

  const achievements = useMemo(() => {
    if (!startedAchievements || !notStartedAchievements) return undefined;

    return uniqBy(
      [...newAchievements, ...startedAchievements, ...notStartedAchievements],
      'achievementId',
    ).sort((a, b) => (b.tier ?? 0) - (a.tier ?? 0));
  }, [startedAchievements, notStartedAchievements, newAchievements]);

  const maxUserAchievements = useMemo(() => {
    if (!startedAchievements || !notStartedAchievements) return undefined;

    const maxAchievementByChain: Record<string, UserAchievement> = {};

    // Process new achievements next, but only if they don't exist in started
    for (const achievement of newAchievements.sort(
      (a, b) => (b.tier ?? 0) - (a.tier ?? 0),
    )) {
      if (!achievement.chainKey) continue;
      if (!maxAchievementByChain[achievement.chainKey]) {
        maxAchievementByChain[achievement.chainKey] = achievement;
      }
    }

    // Process started achievements first (they take precedence)
    for (const achievement of (startedAchievements ?? []).sort(
      (a, b) => (b.tier ?? 0) - (a.tier ?? 0),
    )) {
      if (!achievement.chainKey) continue;
      if (!maxAchievementByChain[achievement.chainKey]) {
        maxAchievementByChain[achievement.chainKey] = achievement;
      }
    }

    // Process not started achievements last
    for (const achievement of (notStartedAchievements ?? []).sort(
      (a, b) => (a.tier ?? 0) - (b.tier ?? 0),
    )) {
      if (!achievement.chainKey) continue;
      if (!maxAchievementByChain[achievement.chainKey]) {
        maxAchievementByChain[achievement.chainKey] = achievement;
      }
    }

    return Object.values(maxAchievementByChain);
  }, [startedAchievements, notStartedAchievements, newAchievements]);

  const tierInfo = useMemo(() => {
    if (!startedAchievements) return null;

    // Combine completed achievements, with started taking precedence over new
    const completedCountsByTier = uniqBy(
      [...newAchievements, ...startedAchievements].filter(
        (ua) => ua.status === UserAchievementStatus.Completed,
      ),
      'achievementId',
    ).reduce(
      (acc, achievement) => {
        if (achievement.tier) {
          acc[achievement.tier] = (acc[achievement.tier] ?? 0) + 1;
        }
        return acc;
      },
      {} as Record<number, number>,
    );

    // Find the current tier
    let currentTier: AchievementTier | null = null;
    let nextTier: AchievementTier | null = null;

    if (
      completedCountsByTier[AchievementTier.Gold] >=
      ACHIEVEMENTS_REQUIRED_BY_TIER[AchievementTier.Gold]
    ) {
      currentTier = AchievementTier.Gold;
      nextTier = null;
    } else if (
      completedCountsByTier[AchievementTier.Silver] >=
      ACHIEVEMENTS_REQUIRED_BY_TIER[AchievementTier.Silver]
    ) {
      currentTier = AchievementTier.Silver;
      nextTier = AchievementTier.Gold;
    } else if (
      completedCountsByTier[AchievementTier.Bronze] >=
      ACHIEVEMENTS_REQUIRED_BY_TIER[AchievementTier.Bronze]
    ) {
      currentTier = AchievementTier.Bronze;
      nextTier = AchievementTier.Silver;
    } else {
      currentTier = null;
      nextTier = AchievementTier.Bronze;
    }

    return {
      tier: currentTier,
      nextTier,
      progress: nextTier ? completedCountsByTier[nextTier] ?? 0 : null,
      required: nextTier ? ACHIEVEMENTS_REQUIRED_BY_TIER[nextTier] : null,
    };
  }, [startedAchievements, newAchievements]);

  const registerUserAchievement = (userAchievement: UserAchievement) => {
    setNewAchievements((prevNewUserAchievements) => [
      ...prevNewUserAchievements,
      userAchievement,
    ]);

    trackEvent(AnalyticsEvent.USER_ACHIEVEMENT_COMPLETED, {
      achievementId: userAchievement.id,
      achievementTitle: userAchievement.title,
    });
  };

  const reloadAchievements = () => {
    setLoadingAt(new Date());
  };

  const getAchievementImage = (userAchievement: UserAchievement) => {
    // If user hasn't started the achievement, show the tier 0 badge
    if (!userAchievement.createdAt) {
      return badgeTier0;
    }

    if (userAchievement.imageUrl) {
      return userAchievement.imageUrl;
    }

    const effectiveTier =
      userAchievement.status !== UserAchievementStatus.Completed
        ? userAchievement.tier - 1 // For incomplete achievements, show previous tier
        : userAchievement.tier; // For completed achievements, show current tier

    // Vite requires static imports for resources
    switch (effectiveTier) {
      case 1:
        return badgeTier1;
      case 2:
        return badgeTier2;
      case 3:
        return badgeTier3;
      default:
        return badgeTier0;
    }
  };

  return (
    <AchievementsContext.Provider
      value={{
        achievements,
        startedAchievements,
        notStartedAchievements,
        registerUserAchievement,
        newAchievements,
        reloadAchievements,
        getAchievementImage,
        maxUserAchievements,
        tierInfo,
        getTierColor,
      }}
    >
      {children}
    </AchievementsContext.Provider>
  );
};

export const useAchievements = (): AchievementsContextType => {
  const context = useContext(AchievementsContext);
  if (context === undefined) {
    throw new Error(
      'useAchievements must be used within an AchievementsProvider',
    );
  }
  return context;
};
